import type { AnchorHTMLAttributes, ButtonHTMLAttributes, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'gold' | 'subtle' | 'danger' | 'link'

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

const base = 'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap font-sans font-medium transition-all duration-300 ease-cinematic select-none disabled:pointer-events-none disabled:opacity-45 [&_svg]:shrink-0'

const variants: Record<ButtonVariant, string> = {
    primary: 'bg-ivory text-obsidian hover:bg-gold rounded-full uppercase tracking-[0.18em]',
    outline: 'border border-ivory/25 text-ivory hover:border-ivory/70 hover:bg-ivory/[0.04] rounded-full uppercase tracking-[0.18em]',
    ghost: 'text-ivory/75 hover:text-ivory hover:bg-ivory/[0.06] rounded-full',
    gold: 'bg-gold text-obsidian hover:bg-ivory rounded-full uppercase tracking-[0.18em]',
    subtle: 'bg-ivory/[0.06] text-ivory hover:bg-ivory/[0.12] rounded-full',
    danger: 'bg-[#5a1f2c] text-ivory hover:bg-burgundy rounded-full',
    link: 'text-gold hover:text-ivory underline-offset-4 hover:underline rounded-sm'
}

const sizes: Record<ButtonSize, string> = {
    sm: 'h-9 px-4 text-[0.68rem] [&_svg]:size-4',
    md: 'h-11 px-6 text-[0.7rem] [&_svg]:size-4',
    lg: 'h-13 px-8 text-[0.72rem] [&_svg]:size-4',
    icon: 'size-11 [&_svg]:size-5',
    'icon-sm': 'size-9 [&_svg]:size-4'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string =>
    cn(base, variants[variant], variant === 'link' ? 'h-auto px-0 normal-case tracking-normal text-sm' : sizes[size], className)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    children?: ReactNode
}

export const Button: FC<ButtonProps> = ({ variant = 'primary', size = 'md', className, type = 'button', children, ...props }): ReactNode => {
    return (
        <button type={type} className={buttonClasses(variant, size, className)} {...props}>
            {children}
        </button>
    )
}

interface ButtonLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    variant?: ButtonVariant
    size?: ButtonSize
    children?: ReactNode
    external?: boolean
}

export const ButtonLink: FC<ButtonLinkProps> = ({ href, variant = 'primary', size = 'md', className, external, children, ...props }): ReactNode => {
    const classes = buttonClasses(variant, size, className)

    if (external || /^https?:\/\//i.test(href) || href.startsWith('mailto:')) {
        return (
            <a href={href} className={classes} target={href.startsWith('mailto:') ? undefined : '_blank'} rel='noopener noreferrer' {...props}>
                {children}
            </a>
        )
    }

    return (
        <Link href={href} className={classes} {...props}>
            {children}
        </Link>
    )
}
